import React from 'react';
import { shallowEqual, useSelector } from 'react-redux';

import { Form, Formik, FormikHelpers } from 'formik';
import { Input, message as Message, Select } from 'antd';
import * as Yup from 'yup';

import moment from 'moment/moment';

import { FormikPhoneControl } from '@common/react/components/Forms/FormikPhoneControl/FormikPhoneControl';
import {
	emailValidator,
	notNullValidator,
	phoneRequiredValidator,
	simpleStringValidator,
} from '@common/react/utils/validationHelpers';
import Button from '@common/react/components/Forms/Button';
import FormikField from '@common/react/components/Forms/FormikField/FormikField';
import { DatePickerReact } from '@common/react/components/UI/DatePickerReact/DatePickerReact';

import { Gender, GenderNames } from '@commonTuna/react/objects/Enums';

import '@app/scss/components/inquiryForm.scss';
import { LocationPortal } from '@app/objects/CompanyPortal';
import { request } from '@app/components/Api';
import { ApplicationState } from '@app/store';

interface InquiryFormValues {
	firstName: string;
	lastName: string;
	phone: string;
	email: string;
	text: string;
	gender: Gender | null;
	locationId: number | null;
	doctorId: number;
}

interface InquiryFormProps {
	doctorId: number;
	locations: Array<LocationPortal>;
	selectedLocationId?: number;
}

const validationSchema = Yup.object().shape({
	locationId: notNullValidator,
	firstName: simpleStringValidator,
	lastName: simpleStringValidator,
	phone: phoneRequiredValidator,
	email: emailValidator,
});

const InquiryForm: React.FC<InquiryFormProps> = ({ doctorId, locations, selectedLocationId }) => {
	const [loading, setLoading] = React.useState<boolean>(false);
	const user = useSelector((state: ApplicationState) => state.login.user, shallowEqual);

	const handleSubmit = React.useCallback((values: InquiryFormValues, formikHelpers: FormikHelpers<InquiryFormValues>) => {
		setLoading(true);

		request('sendInquiry', { ...values, name: `${values.firstName} ${values.lastName}` })
			.then(() => {
				setLoading(false);
				Message.success('Success');
				formikHelpers.resetForm();
			})
			.catch((message) => {
				Message.error('Something went wrong!');
				console.error(message);
				setLoading(false);
			});
	}, []);

	const showDateOfBirth = locations[0]?.company?.showDateOfBirth;

	return (
		<div className="inquiry-form">
			<Formik<InquiryFormValues>
				initialValues={{
					firstName: user?.firstName || '',
					lastName: user?.lastName || '',
					phone: user?.phoneNumber || '',
					email: user?.email || '',
					text: '',
					gender: user?.gender ?? Gender.Female,
					locationId: selectedLocationId || (locations?.length > 0 ? locations[0].id : null),
					doctorId,
				}}
				validationSchema={validationSchema}
				onSubmit={handleSubmit}
			>
				<Form>
					<div className="row">
						<FormikField
							title="First Name*"
							fieldName="firstName"
							inputProps={{
								maxLength: 50,
							}}
							containerClassName="form-group col-sm-12"
						/>
					</div>
					<div className="row">
						<FormikField
							title="Last Name*"
							fieldName="lastName"
							inputProps={{
								maxLength: 50,
							}}
							containerClassName="form-group col-sm-12"
						/>
					</div>
					<div className="row">
						<FormikField
							fieldName="phone"
							title="Phone Number*"
							containerClassName="form-group col-sm-12"
							render={(fieldProps) =>
								<FormikPhoneControl
									fieldProps={fieldProps}
									placeholder="+1 (___) ___-____"
								/>
							}
						/>
					</div>
					<div className="row">
						<FormikField
							title="Email*"
							fieldName="email"
							containerClassName="form-group col-sm-12"
						/>
					</div>
					{showDateOfBirth
						&& <div className="row">
							<FormikField
								fieldName="birthDate"
								title="Birth Date"
								containerClassName="form-group col-sm-12"
								render={({ field, form }) => (
									<DatePickerReact
										value={field.value}
										onChange={(date) => form.setFieldValue(field.name, date)}
										datePickerProps={{
											showMonthDropdown: true,
											showYearDropdown: true,
											dropdownMode: 'select',
											maxDate: moment().toDate(),
											minDate: moment().add(-100, 'years').toDate(),
										}}
										utc
									/>
								)}
							/>
						</div>}
					<div className="row">
						<FormikField
							title="Gender*"
							fieldName="gender"
							containerClassName="form-group col-sm-12"
							render={({ field, form }) => <>
								<Select
									placeholder="Gender"
									defaultValue={Gender.Female}
									onChange={(value) => {
										form.setFieldValue(field.name, +value);
									}}
									value={GenderNames[field.value]}
								>

									{Object.keys(GenderNames).map((key) =>
										<Select.Option value={key} key={key}>{GenderNames[key]}</Select.Option>)}
								</Select>
							</>
							}
						/>
					</div>
					<div className="row">
						<FormikField
							title="Location*"
							fieldName="locationId"
							containerClassName="form-group col-sm-12"
							render={({ field, form }) =>
								<Select
									placeholder="Location"
									onChange={(value) => form.setFieldValue(field.name, value)}
									value={field.value}
								>
									{locations?.map((location) => <Select.Option key={`location-${location.id}`} value={location.id}>
										{location.company ? location.company.name : 'Unknown'}
										{' '}
										-
										{' '}
										{location.nameEn}
									</Select.Option>)}
								</Select>
							}
						/>
					</div>
					<div className="row">
						<FormikField
							title="Text"
							fieldName="text"
							containerClassName="form-group col-sm-12"
							render={({ field }) =>
								<Input.TextArea
									className="form-control"
									maxLength={500}
									style={{ height: 120, resize: 'none' }}
									{...field}
								/>
							}
						/>
					</div>
					<div className="text-center">
						<Button
							isLoading={loading}
							type="submit"
							className="btn btn-primary general-form-button"
						>
							Submit
						</Button>
					</div>
				</Form>
			</Formik>
		</div>
	);
};

export default InquiryForm;
